import React, { useState, useEffect } from 'react';
import { Search, Filter, MapPin, Briefcase, GraduationCap, Calendar } from 'lucide-react';
import { Profile, Filters } from '../../types';
import { DROPDOWN_OPTIONS } from '../../utils/constants';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ProfileCard from '../../components/profile/ProfileCard';

const Profiles: React.FC = () => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<Filters>({
    lookingFor: '',
    birthYearFrom: '',
    birthYearTo: '',
    maritalStatus: '',
    workState: '',
    workCity: '',
    familyState: '',
    familyCity: '',
    incomeFrom: '',
    incomeTo: ''
  });

  useEffect(() => {
    loadProfiles();
  }, [currentPage, filters]);

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const response = await apiService.getProfiles(filters, currentPage);
      setProfiles(response.profiles || []);
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      console.error('Failed to load profiles:', error);
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      lookingFor: '',
      birthYearFrom: '',
      birthYearTo: '',
      maritalStatus: '',
      workState: '',
      workCity: '',
      familyState: '',
      familyCity: '',
      incomeFrom: '',
      incomeTo: ''
    });
    setCurrentPage(1);
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 60; year <= currentYear - 18; year++) {
      years.push({ value: year.toString(), label: year.toString() });
    }
    return years.reverse();
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value !== '').length;
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Browse Profiles</h1>
          <p className="text-gray-600">
            Find your perfect match from verified profiles in our community
          </p>
        </div>
        
        <div className="flex items-center space-x-4 mt-4 md:mt-0">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 bg-white border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
            {getActiveFiltersCount() > 0 && (
              <span className="bg-primary-600 text-white text-xs rounded-full px-2 py-1">
                {getActiveFiltersCount()}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Search Filters</h3>
            <button
              onClick={clearFilters}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Clear All
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Looking For */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Looking For
              </label>
              <select
                name="lookingFor"
                value={filters.lookingFor}
                onChange={handleFilterChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Any</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            {/* Birth Year From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Birth Year From
              </label>
              <select
                name="birthYearFrom"
                value={filters.birthYearFrom}
                onChange={handleFilterChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Any</option>
                {generateYearOptions().map(year => (
                  <option key={year.value} value={year.value}>{year.label}</option>
                ))}
              </select>
            </div>

            {/* Birth Year To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Birth Year To
              </label>
              <select
                name="birthYearTo"
                value={filters.birthYearTo}
                onChange={handleFilterChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Any</option>
                {generateYearOptions().map(year => (
                  <option key={year.value} value={year.value}>{year.label}</option>
                ))}
              </select>
            </div>

            {/* Marital Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marital Status
              </label>
              <select
                name="maritalStatus"
                value={filters.maritalStatus}
                onChange={handleFilterChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Any</option>
                {DROPDOWN_OPTIONS.maritalStatus.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* Work State */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Work State
              </label>
              <select
                name="workState"
                value={filters.workState}
                onChange={handleFilterChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Any</option>
                {DROPDOWN_OPTIONS.indianStates.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* Family State */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Family State
              </label>
              <select
                name="familyState"
                value={filters.familyState}
                onChange={handleFilterChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Any</option>
                {DROPDOWN_OPTIONS.indianStates.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* Annual Income From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Income From
              </label>
              <select
                name="incomeFrom"
                value={filters.incomeFrom}
                onChange={handleFilterChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Any</option>
                {DROPDOWN_OPTIONS.annualIncome.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* Annual Income To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Income To
              </label>
              <select
                name="incomeTo"
                value={filters.incomeTo}
                onChange={handleFilterChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Any</option>
                {DROPDOWN_OPTIONS.annualIncome.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="mb-4">
        <p className="text-sm text-gray-600">
          {!loading && `Showing ${profiles.length} profiles`}
        </p>
      </div>

      {/* Profiles Grid */}
      {loading ? (
        <LoadingSpinner text="Loading profiles..." />
      ) : profiles.length === 0 ? (
        <div className="text-center py-12">
          <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No profiles found</h3>
          <p className="text-gray-600 mb-4">
            Try adjusting your filters to see more results
          </p>
          <button
            onClick={clearFilters}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Clear all filters
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {profiles.map(profile => (
              <ProfileCard key={profile.id} profile={profile} />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => 
                    page === 1 || 
                    page === totalPages || 
                    Math.abs(page - currentPage) <= 2
                  )
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span className="px-2 text-gray-400">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-2 border rounded-lg ${
                          currentPage === page
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Profiles;